// Door lock tile with an inline PIN popover (no side panel). Clicking the tile
// opens a small "Enter PIN" patch ABOVE it; a correct PIN (validated against the
// logged-in user's password, like DoorScreen) publishes the unlock command
// (OPENDOOR on 3, LOCKUNLOCK on 102).
// Fewer clicks (2026-10-20): on lock 3 the 4th correct digit opens it and the
// popover closes itself with a toast; 102 keeps "type, then press".

import React, { useState } from 'react';
import {
  Card, CardActionArea, Box, Typography, Popover, TextField, Button, Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useApp } from '../../../core/state/AppContext';
import { publishCommand } from '../../../core/utils/deviceController';
import { unlockCommand, unlockLabel, ROOM_DOOR_LOCK_ID } from '../../../core/devices/deviceMeta';
import { showToast } from '../../shared/toast';
import DeviceStatusDot from './DeviceStatusDot';

export default function DoorControlCard({ device, subtitle }) {
  const { userId, user } = useApp();
  const uno = device.UniqueNumber;
  const [anchor, setAnchor] = useState(null);
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState(null);

  const openPopover = (e) => { setPin(''); setMsg(null); setAnchor(e.currentTarget); };
  const closePopover = () => setAnchor(null);

  const explicitButton = Number(device.DeviceID) === ROOM_DOOR_LOCK_ID;

  const unlock = (entered = pin) => {
    if (entered === String(user?.Password ?? '')) {
      publishCommand(uno, unlockCommand(device.DeviceID), userId);
      setPin('');
      setAnchor(null);
      showToast('Unlock sent to the lock', { severity: 'success' });
    } else {
      setMsg({ type: 'error', text: 'Incorrect PIN.' });
      setPin('');
    }
  };

  const name = device.DeviceName || uno;

  return (
    <>
      <Card sx={{ bgcolor: 'primary.main', borderRadius: 3, height: '100%' }}>
        {/* 1.25 to match every other card on the strip. At 2 this one was the
            odd tall block even once the stretch was gone. */}
        <CardActionArea onClick={openPopover} sx={{ p: 1.25, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: 'primary.contrastText' }}>
            {subtitle && (
              <Typography variant="caption" noWrap sx={{ opacity: 0.75, maxWidth: '100%' }}>{subtitle}</Typography>
            )}
            <LockIcon fontSize="large" />
            <Typography variant="body1" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>{name}</Typography>
            <DeviceStatusDot device={device} />
          </Box>
        </CardActionArea>
      </Card>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 220, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOpenIcon fontSize="small" color="inherit" />
            <Typography variant="subtitle2" color="text.primary">{explicitButton ? 'Enter PIN, then press the button' : 'Enter your PIN'}</Typography>
          </Box>
          {msg && <Alert severity={msg.type} sx={{ py: 0 }}>{msg.text}</Alert>}
          <TextField
            type="password" value={pin} label="PIN" size="small" autoFocus fullWidth
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, '').slice(0, 4);
              setPin(next);
              if (next.length === 4 && !explicitButton) unlock(next);
            }}
            onKeyDown={(e) => e.key === 'Enter' && unlock()}
            inputProps={{ inputMode: 'numeric', maxLength: 4 }}
          />
          <Button variant="contained" fullWidth startIcon={<LockOpenIcon />} onClick={() => unlock()}>
            {unlockLabel(device.DeviceID)}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
